'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { Button } from '@/components/common/inputs/button/button';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/common/overlay/dialog/dialog';

import { cn } from '@/lib/utils';

interface PaymentStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  status: 'success' | 'failed';
}

export const PaymentStatusDialog = ({
  open,
  onOpenChange,
  status,
}: PaymentStatusDialogProps) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const handleOpenChange = (newOpen: boolean) => {
    onOpenChange(newOpen);
    if (!newOpen) {
      const params = new URLSearchParams(searchParams?.toString());
      params.delete('paymentCompleted');
      params.delete('success');
      params.delete('canceled');
      params.delete('payment_success');
      params.delete('paymentSuccess');

      const queryString = params.toString();
      const newUrl = queryString ? `${pathname}?${queryString}` : pathname;
      router.replace(newUrl, { scroll: false });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogTitle
          className={cn(
            'text-lg font-semibold',
            status === 'success' ? 'text-green-600' : 'text-red-600'
          )}
        >
          {status === 'success'
            ? 'Subscription Successful!'
            : 'Subscription Cancelled'}
        </DialogTitle>
        <DialogDescription className='mt-2 text-gray-600'>
          {status === 'success'
            ? 'Your subscription has been successfully activated.'
            : 'Your subscription process was not completed.'}
        </DialogDescription>
        <div className='mt-4 flex flex-col items-center justify-center space-y-4'>
          {status === 'success' ? (
            <>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-green-100'>
                <svg
                  className='h-6 w-6 text-green-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M5 13l4 4L19 7'
                  />
                </svg>
              </div>
              <p className='text-center text-gray-600'>
                Welcome to Jaqln Pro! Your subscription is now active.
              </p>
              <Button
                onClick={() => handleOpenChange(false)}
                className='w-full bg-gradient-to-l from-purple-400 to-purple-600 text-white hover:from-purple-500 hover:to-purple-700'
              >
                Start Using Pro Features
              </Button>
            </>
          ) : (
            <>
              <div className='flex h-12 w-12 items-center justify-center rounded-full bg-red-100'>
                <svg
                  className='h-6 w-6 text-red-600'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M6 18L18 6M6 6l12 12'
                  />
                </svg>
              </div>
              <p className='text-center text-gray-600'>
                The subscription process was cancelled. Please try again if you
                wish to upgrade.
              </p>
              <Button
                onClick={() => handleOpenChange(false)}
                className='w-full bg-gray-600 text-white hover:bg-gray-700'
              >
                Close
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
